import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, gbp, downloadFile, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { FileSpreadsheet, FileDown, Filter, Loader2, Undo2, TrendingUp, Split, ShoppingBag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DetailPanel } from "@/admin/DetailPanel";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

function Row({ label, value, strong, accent }) {
  return (
    <div className={`flex items-center justify-between py-2.5 ${strong ? "border-t border-slate-200 dark:border-slate-700" : "border-b border-slate-100 dark:border-slate-800"}`}>
      <span className={`text-sm ${strong ? "font-semibold text-slate-900 dark:text-white" : "text-slate-500"}`}>{label}</span>
      <span className={`font-mono-x ${strong ? "text-lg font-bold" : "text-sm font-medium"} ${accent || "text-slate-900 dark:text-white"}`}>{value}</span>
    </div>
  );
}

export default function Financials() {
  const [buildings, setBuildings] = useState([]);
  const [kiosks, setKiosks] = useState([]);
  const [filters, setFilters] = useState({ start: "", end: "", building_id: "all", kiosk_id: "all", category: "all" });
  const [data, setData] = useState(null);
  const [txns, setTxns] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/buildings").then((r) => setBuildings(r.data));
    api.get("/kiosks").then((r) => setKiosks(r.data));
    api.get("/payments/transactions").then((r) => setTxns(r.data)).catch(() => {});
  }, []);

  const buildQuery = () => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => { if (v && v !== "all") params.append(k, v); });
    return params.toString();
  };

  const run = () => {
    setLoading(true);
    api.get(`/reports/dashboard?${buildQuery()}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  };
  useEffect(() => { run(); }, []);

  const doPayoutExport = async (fmt) => {
    try {
      const q = buildQuery();
      await downloadFile(`/reports/payouts/export?fmt=${fmt}${q ? "&" + q : ""}`, `rebate_charity_report.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const doExport = async (fmt) => {
    try {
      const q = buildQuery();
      await downloadFile(`/reports/export?fmt=${fmt}${q ? "&" + q : ""}`, `sales_report.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error("Export failed");
    }
  };

  const [refundTxn, setRefundTxn] = useState(null);
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [searchParams] = useSearchParams();
  useEffect(() => {
    const tid = searchParams.get("txn");
    if (tid && txns.length) {
      const t = txns.find((x) => (x.transaction_id || x.id) === tid);
      if (t) setSelectedTxn(t);
    }
  }, [txns, searchParams]);
  const txnStatus = (t) => t.refunded
    ? { label: "Refunded", cls: "text-red-500" }
    : t.refund_amount > 0
      ? { label: `Partial (${gbp(t.refund_amount)})`, cls: "text-amber-600" }
      : { label: "Paid", cls: "text-emerald-600" };

  const doRefund = async (t, item, qty) => {
    try {
      const body = { session_id: t.session_id };
      if (item) { body.product_id = item.product_id; body.quantity = qty || item.quantity; }
      await api.post("/payments/refund", body);
      toast.success("Refund processed");
      const r = await api.get("/payments/transactions");
      setTxns(r.data);
      setRefundTxn(r.data.find((x) => x.session_id === t.session_id) || null);
      run();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const m = data?.metrics;

  return (
    <div className="p-6 lg:p-8 space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-admin-head text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">Financial Reporting</h1>
          <p className="text-slate-500 mt-1">VAT, margins, Stripe fees & exports</p>
        </div>
        <div className="flex gap-2">
          <Button data-testid="admin-export-csv-button" variant="outline" onClick={() => doExport("csv")}><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
          <Button data-testid="admin-export-xlsx-button" variant="outline" onClick={() => doExport("xlsx")}><FileSpreadsheet className="h-4 w-4 mr-1.5" /> XLSX</Button>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4">
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-6 items-end">
          <div><Label className="text-xs">From</Label><Input data-testid="report-filter-start" type="date" value={filters.start} onChange={(e) => setFilters({ ...filters, start: e.target.value })} className="mt-1" /></div>
          <div><Label className="text-xs">To</Label><Input data-testid="report-filter-end" type="date" value={filters.end} onChange={(e) => setFilters({ ...filters, end: e.target.value })} className="mt-1" /></div>
          <div>
            <Label className="text-xs">Building</Label>
            <Select value={filters.building_id} onValueChange={(v) => setFilters({ ...filters, building_id: v })}>
              <SelectTrigger data-testid="report-filter-building" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All buildings</SelectItem>
                {buildings.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Kiosk</Label>
            <Select value={filters.kiosk_id} onValueChange={(v) => setFilters({ ...filters, kiosk_id: v })}>
              <SelectTrigger data-testid="report-filter-kiosk" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All kiosks</SelectItem>
                {kiosks.map((k) => <SelectItem key={k.id} value={k.id}>{k.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={filters.category} onValueChange={(v) => setFilters({ ...filters, category: v })}>
              <SelectTrigger data-testid="report-filter-category" className="mt-1"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                <SelectItem value="Snacks">Snacks</SelectItem>
                <SelectItem value="Drinks">Drinks</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="report-apply-button" onClick={run} className="bg-blue-600 hover:bg-blue-700"><Filter className="h-4 w-4 mr-1.5" /> Apply</Button>
        </div>
      </div>

      {loading || !m ? (
        <div className="h-40 flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Payment & VAT summary</h3>
            <Row label="Gross customer payment" value={gbp(m.gross_sales)} />
            <Row label="VAT (output tax)" value={gbp(m.vat)} />
            <Row label="Net sales (excl VAT)" value={gbp(m.net_sales)} strong accent="text-blue-600" />
            <Row label="Stripe processing fees" value={`- ${gbp(m.processing_fees)}`} accent="text-red-500" />
            <Row label="Net proceeds after fees" value={gbp(m.revenue_after_fees)} strong accent="text-emerald-600" />
          </div>
          <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Profitability</h3>
            <Row label="Cost of goods sold (COGS)" value={gbp(m.cogs)} />
            <Row label="Gross profit (Net − COGS)" value={gbp(m.gross_profit)} strong accent="text-blue-600" />
            <Row label="Contribution after fees" value={gbp(m.contribution_after_fees)} strong accent="text-emerald-600" />
            <Row label="Transactions" value={m.transactions} />
            <Row label="Units sold" value={m.units_sold} />
            <Row label="Avg transaction value" value={gbp(m.avg_transaction_value)} />
            <Row label="Failed / cancelled" value={m.failed_transactions} />
            <Row label="Refunds" value={`${m.refunds} · ${gbp(m.refunds_amount || 0)}`} accent="text-red-500" />
            <Row label="Net proceeds after refunds" value={gbp(m.net_after_refunds ?? m.revenue_after_fees)} strong accent="text-emerald-600" />
          </div>

          <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6" data-testid="rebate-charity-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-semibold text-slate-900 dark:text-white flex items-center gap-2"><TrendingUp className="h-4 w-4 text-indigo-500" /> Rebate &amp; Charity (payouts)</h3>
              <div className="flex gap-2">
                <Button data-testid="payouts-export-csv" size="sm" variant="outline" onClick={() => doPayoutExport("csv")}><FileDown className="h-4 w-4 mr-1.5" /> CSV</Button>
                <Button data-testid="payouts-export-xlsx" size="sm" variant="outline" onClick={() => doPayoutExport("xlsx")}><FileSpreadsheet className="h-4 w-4 mr-1.5" /> XLSX</Button>
              </div>
            </div>
            <div className="flex flex-wrap gap-6 mb-4 text-sm">
              <div><span className="text-slate-400">Total rebate</span><div className="text-lg font-bold text-blue-600">{gbp(m.rebate_total || 0)}</div></div>
              <div><span className="text-slate-400">Total to charity</span><div className="text-lg font-bold text-emerald-600">{gbp(m.charity_total || 0)}</div></div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100 dark:border-slate-800">
                    <th className="py-2 font-medium">Building</th>
                    <th className="py-2 font-medium">Group</th>
                    <th className="py-2 font-medium text-right">Net sales</th>
                    <th className="py-2 font-medium text-right">Rebate</th>
                    <th className="py-2 font-medium">Charity</th>
                    <th className="py-2 font-medium text-right">To charity</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.payouts || []).map((p) => (
                    <tr key={p.building_id} data-testid={`payout-${p.building_id}`} className="border-b border-slate-50 dark:border-slate-800/50">
                      <td className="py-2 text-slate-700 dark:text-slate-300">{p.building_name}</td>
                      <td className="py-2 text-slate-500">{p.group || "—"}</td>
                      <td className="py-2 text-right">{gbp(p.net_sales)}</td>
                      <td className="py-2 text-right font-semibold text-blue-600">{gbp(p.rebate)}</td>
                      <td className="py-2 text-slate-500">{p.charity_name || "—"}</td>
                      <td className="py-2 text-right font-semibold text-emerald-600">{gbp(p.charity)}</td>
                    </tr>
                  ))}
                  {!(data.payouts || []).length && <tr><td colSpan={6} className="py-6 text-center text-slate-400">No sales in this period.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-3">Product performance</h3>
            <div className="space-y-1">
              {data.top_products.length ? data.top_products.map((p) => (
                <div key={p.product_id} className="flex items-center justify-between py-2 border-b border-slate-100 dark:border-slate-800 text-sm">
                  <span className="text-slate-700 dark:text-slate-300">{p.name}</span>
                  <span className="text-slate-500">{p.units} units · <span className="font-semibold text-slate-900 dark:text-white">{gbp(p.revenue)}</span></span>
                </div>
              )) : <p className="text-sm text-slate-400">No sales in this period.</p>}
            </div>
          </div>

          <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-blue-600" /> Meal deal performance</h3>
            <p className="text-xs text-slate-400 mb-3">How each deal lifts the average basket (overall avg {gbp(m.avg_transaction_value)}).</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100 dark:border-slate-800">
                    <th className="py-2 font-medium">Deal</th>
                    <th className="py-2 font-medium text-right">Orders</th>
                    <th className="py-2 font-medium text-right">Units</th>
                    <th className="py-2 font-medium text-right">Revenue</th>
                    <th className="py-2 font-medium text-right">Avg basket w/ deal</th>
                    <th className="py-2 font-medium text-right">Basket uplift</th>
                  </tr>
                </thead>
                <tbody>
                  {(data.deal_performance || []).map((d) => (
                    <tr key={d.combo_id} data-testid={`deal-perf-${d.combo_id}`} className="border-b border-slate-50 dark:border-slate-800/50">
                      <td className="py-2 text-slate-700 dark:text-slate-300">{d.name}</td>
                      <td className="py-2 text-right">{d.orders}</td>
                      <td className="py-2 text-right">{d.units}</td>
                      <td className="py-2 text-right font-semibold text-slate-900 dark:text-white">{gbp(d.revenue)}</td>
                      <td className="py-2 text-right">{gbp(d.avg_basket_with_deal)}</td>
                      <td className={`py-2 text-right font-semibold ${d.basket_uplift >= 0 ? "text-emerald-600" : "text-red-500"}`}>{d.basket_uplift >= 0 ? "+" : ""}{gbp(d.basket_uplift)}</td>
                    </tr>
                  ))}
                  {!(data.deal_performance || []).length && (
                    <tr><td colSpan={6} className="py-6 text-center text-slate-400">No meal-deal sales in this period.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-1 flex items-center gap-2"><Split className="h-4 w-4 text-indigo-600" /> Deal A/B test results</h3>
            <p className="text-xs text-slate-400 mb-3">Which variant lifts the basket more (winner by average basket).</p>
            <div className="space-y-4">
              {(data.deal_ab || []).map((d) => (
                <div key={d.combo_id} data-testid={`deal-ab-${d.combo_id}`} className="rounded-lg border border-slate-100 dark:border-slate-800 p-3">
                  <p className="font-medium text-slate-900 dark:text-white mb-2">{d.name}</p>
                  <div className="grid grid-cols-2 gap-3">
                    {d.variants.map((v) => (
                      <div key={v.variant} className={`rounded-lg p-3 border ${d.winner === v.variant ? "border-emerald-400 bg-emerald-50 dark:bg-emerald-950/30" : "border-slate-200 dark:border-slate-700"}`}>
                        <div className="flex items-center justify-between">
                          <span className="font-semibold">Variant {v.variant}</span>
                          {d.winner === v.variant && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white">WINNER</span>}
                        </div>
                        <p className="text-xs text-slate-500 mt-1">{v.orders} orders · {v.units} units</p>
                        <p className="text-sm mt-1">Avg basket <span className="font-bold">{gbp(v.avg_basket)}</span></p>
                        <p className="text-xs text-slate-400">Revenue {gbp(v.revenue)}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
              {!(data.deal_ab || []).length && <p className="text-sm text-slate-400">No A/B deals with sales in both variants yet.</p>}
            </div>
          </div>

          <div className="lg:col-span-2 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-6">
            <h3 className="font-semibold text-slate-900 dark:text-white mb-3 flex items-center gap-2"><Undo2 className="h-4 w-4 text-blue-600" /> Transactions & refunds</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-slate-400 border-b border-slate-100 dark:border-slate-800">
                    <th className="py-2 font-medium">When</th>
                    <th className="py-2 font-medium">Items</th>
                    <th className="py-2 font-medium text-right">Amount</th>
                    <th className="py-2 font-medium">Status</th>
                    <th className="py-2 font-medium text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {txns.map((t) => (
                    <tr key={t.session_id} data-testid={`txn-row-${t.transaction_id}`} onClick={() => setSelectedTxn(t)} className="border-b border-slate-50 dark:border-slate-800/50 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/50">
                      <td className="py-2 text-xs text-slate-500">{(t.created_at || "").slice(0, 16).replace("T", " ")}</td>
                      <td className="py-2 text-slate-700 dark:text-slate-300">{t.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}</td>
                      <td className="py-2 text-right font-semibold">{gbp(t.gross)}</td>
                      <td className="py-2">
                        {t.refunded
                          ? <span className="text-xs font-semibold text-red-500">Refunded</span>
                          : t.refund_amount > 0
                            ? <span className="text-xs font-semibold text-amber-600">Partial ({gbp(t.refund_amount)})</span>
                            : <span className="text-xs font-semibold text-emerald-600">Paid</span>}
                      </td>
                      <td className="py-2 text-right">
                        <Button data-testid={`refund-button-${t.transaction_id}`} variant="outline" size="sm" disabled={t.refunded} onClick={(e) => { e.stopPropagation(); setRefundTxn(t); }}>
                          <Undo2 className="h-3.5 w-3.5 mr-1" /> Refund
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {!txns.length && <tr><td colSpan={5} className="py-6 text-center text-slate-400">No completed transactions yet.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      <DetailPanel
        open={!!selectedTxn}
        onClose={() => setSelectedTxn(null)}
        eyebrow="Order"
        title={selectedTxn ? `#${selectedTxn.transaction_id}` : ""}
        icon={ShoppingBag}
        actions={selectedTxn && (
          <Button data-testid="detail-txn-refund" variant="outline" size="sm" disabled={selectedTxn.refunded} onClick={() => { setRefundTxn(selectedTxn); setSelectedTxn(null); }}>
            <Undo2 className="h-4 w-4 mr-1.5" /> Refund
          </Button>
        )}
      >
        {selectedTxn && (
          <div className="space-y-5">
            <div className="flex items-center justify-between">
              <span className={`text-sm font-semibold ${txnStatus(selectedTxn).cls}`}>{txnStatus(selectedTxn).label}</span>
              <span className="text-xs text-slate-400">{(selectedTxn.created_at || "").slice(0, 16).replace("T", " ")}</span>
            </div>
            <div>
              <p className="text-xs uppercase tracking-wide text-slate-400 mb-2">Items</p>
              <div className="space-y-2">
                {selectedTxn.items.map((i, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-800 px-3 py-2 text-sm">
                    <div>
                      <span className="text-slate-800 dark:text-slate-200">{i.quantity}× {i.name}</span>
                      <span className="block text-xs text-slate-400">{gbp(i.unit_price_incl)} each</span>
                    </div>
                    <span className="font-semibold">{gbp(i.unit_price_incl * i.quantity)}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 p-4">
              <Row label="Gross paid" value={gbp(selectedTxn.gross)} />
              <Row label="Refunded" value={gbp(selectedTxn.refund_amount || 0)} accent="text-red-500" />
              <Row label="Remaining refundable" value={gbp(selectedTxn.refundable)} strong accent="text-emerald-600" />
            </div>
          </div>
        )}
      </DetailPanel>

      <Dialog open={!!refundTxn} onOpenChange={(o) => !o && setRefundTxn(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Refund transaction</DialogTitle></DialogHeader>
          {refundTxn && (
            <div className="space-y-3">
              <p className="text-sm text-slate-500">Total {gbp(refundTxn.gross)} · Refunded {gbp(refundTxn.refund_amount)} · Remaining {gbp(refundTxn.refundable)}</p>
              <div className="space-y-2">
                {refundTxn.items.map((i, idx) => (
                  <div key={idx} className="flex items-center justify-between rounded-lg border border-slate-200 dark:border-slate-700 px-3 py-2">
                    <div className="text-sm">
                      <span className="text-slate-800 dark:text-slate-200">{i.quantity}× {i.name}</span>
                      <span className="block text-xs text-slate-400">{gbp(i.unit_price_incl)} each</span>
                    </div>
                    <Button data-testid={`refund-line-${i.product_id}`} variant="outline" size="sm" disabled={refundTxn.refunded} onClick={() => doRefund(refundTxn, i, i.quantity)}>
                      Refund {gbp(i.unit_price_incl * i.quantity)}
                    </Button>
                  </div>
                ))}
              </div>
              <Button data-testid="refund-all-button" className="w-full bg-red-600 hover:bg-red-700" disabled={refundTxn.refunded} onClick={() => doRefund(refundTxn)}>
                Refund remaining {gbp(refundTxn.refundable)}
              </Button>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRefundTxn(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
